import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

const TOUR_STEPS = [
  {
    target: '[data-tour="projects"]',
    title: 'Your Projects',
    description: 'All your active projects live here. Track budgets, phases, and orders at a glance.',
    position: 'bottom',
  },
  {
    target: '[data-tour="new-project"]',
    title: 'Create a Project',
    description: 'Tap here to start a new project. Add a name, client, budget and you\'re off.',
    position: 'left',
  },
  {
    target: '[data-tour="vendors"]',
    title: 'Vendor Intelligence',
    description: 'Automatic scorecards, spend analytics, and delivery tracking for every vendor you order from.',
    position: 'right',
  },
  {
    target: '[data-tour="inbox"]',
    title: 'Procurement Inbox',
    description: 'Upload quotes, invoices, and packing slips. Attach them to projects and orders to keep everything in one place.',
    position: 'right',
  },
  {
    target: '[data-tour="alerts"]',
    title: 'Smart Alerts',
    description: 'Get notified about budget overruns, late deliveries, and upcoming phases — automatically.',
    position: 'right',
  },
];

const STORAGE_KEY = 'punchlist_tour_seen';

const QuickTour = ({ userId }) => {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const seen = sessionStorage.getItem(`${STORAGE_KEY}_${userId}`);
    if (!seen) {
      const timer = setTimeout(() => setActive(true), 1200);
      return () => clearTimeout(timer);
    }
  }, [userId]);

  const measureTarget = useCallback(() => {
    if (!active) return;
    const target = document.querySelector(TOUR_STEPS[step].target);
    if (target) {
      const r = target.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      setRect(null);
    }
  }, [active, step]);

  useEffect(() => {
    measureTarget();
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, true);
    return () => {
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget, true);
    };
  }, [measureTarget]);

  const dismiss = useCallback(() => {
    setActive(false);
    sessionStorage.setItem(`${STORAGE_KEY}_${userId}`, 'true');
  }, [userId]);

  const next = () => {
    if (step < TOUR_STEPS.length - 1) setStep(step + 1);
    else dismiss();
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  if (!active || !rect) return null;

  const pad = 6;
  const current = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;

  let tooltipStyle = {};
  const pos = current.position;
  if (pos === 'bottom') {
    tooltipStyle = { top: rect.top + rect.height + pad + 12, left: rect.left, maxWidth: 340 };
  } else if (pos === 'top') {
    tooltipStyle = { bottom: window.innerHeight - rect.top + pad + 12, left: rect.left, maxWidth: 340 };
  } else if (pos === 'right') {
    tooltipStyle = { top: rect.top, left: rect.left + rect.width + pad + 12, maxWidth: 320 };
  } else if (pos === 'left') {
    tooltipStyle = { top: rect.top, right: window.innerWidth - rect.left + pad + 12, maxWidth: 320 };
  }

  if (tooltipStyle.left !== undefined && tooltipStyle.left + 340 > window.innerWidth) {
    tooltipStyle.left = Math.max(12, window.innerWidth - 352);
  }
  if (tooltipStyle.top !== undefined && tooltipStyle.top + 200 > window.innerHeight) {
    tooltipStyle.top = Math.max(12, window.innerHeight - 220);
  }

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="quick-tour-overlay">
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={rect.left - pad}
              y={rect.top - pad}
              width={rect.width + pad * 2}
              height={rect.height + pad * 2}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#tour-mask)"
        />
      </svg>

      <div
        className="absolute border-2 border-primary pointer-events-none"
        style={{
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
          boxShadow: '0 0 0 3px rgba(255,196,12,0.3)',
          transition: 'all 0.3s ease',
        }}
      />

      <div className="absolute inset-0" onClick={dismiss} />

      <div
        ref={tooltipRef}
        className="absolute bg-white border border-zinc-200 shadow-lg p-5 z-10"
        style={{ ...tooltipStyle, transition: 'all 0.3s ease' }}
        data-testid={`tour-tooltip-step-${step}`}
      >
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 p-1 text-zinc-400 hover:text-zinc-700"
          data-testid="tour-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-1.5 mb-3">
          {TOUR_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 transition-all ${
                i === step ? 'w-6 bg-primary' : i < step ? 'w-3 bg-primary/50' : 'w-3 bg-zinc-200'
              }`}
            />
          ))}
        </div>

        <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-zinc-900 mb-1">{current.title}</h3>
        <p className="text-sm text-zinc-500 leading-relaxed mb-4">{current.description}</p>

        <div className="flex items-center justify-between">
          <button
            onClick={dismiss}
            className="text-xs font-medium text-zinc-400 hover:text-zinc-700 transition-colors uppercase tracking-wide"
            data-testid="tour-skip-btn"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={prev}
                className="h-8 px-3 border border-zinc-200 bg-white text-zinc-600 text-xs font-heading font-semibold uppercase tracking-wider flex items-center gap-1 hover:bg-zinc-50 transition-colors"
                data-testid="tour-prev-btn"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            <button
              onClick={next}
              className="h-8 px-4 bg-primary text-black text-xs font-heading font-bold uppercase tracking-wider flex items-center gap-1 hover:bg-primary-hover transition-colors"
              data-testid="tour-next-btn"
            >
              {isLast ? 'Done' : 'Next'} {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickTour;
